import { cloneElement, forwardRef, Fragment, isValidElement, useState } from 'react';
import { createPortal } from 'react-dom';

import { useForkRef, useIsomorphicEffect } from '@spazious/react-hooks';

import { setRef } from '../../utils/index.js';

export interface PortalProps {
  /**
   * The children to render into the `container`.
   */
  children?: React.ReactNode;
  /**
   * An HTML element or function that returns one.
   * The `container` will have the portal children appended to it.
   *
   * By default, it uses the body of the top-level document object,
   * so it's simply `document.body` most of the time.
   */
  container?: Element | (() => Element | null) | null;
  /**
   * The `children` will be under the DOM hierarchy of the parent component.
   * @default false
   */
  disablePortal?: boolean;
}

function getContainer(container: PortalProps['container']) {
  return typeof container === 'function' ? container() : container;
}

/**
 * Portals provide a first-class way to render children into a DOM node
 * that exists outside the DOM hierarchy of the parent component.
 */
export const Portal = forwardRef((props: PortalProps, ref: React.ForwardedRef<Element>) => {
  const { children, container, disablePortal = false } = props;
  const [mountNode, setMountNode] = useState<ReturnType<typeof getContainer>>(null);
  const handleRef = useForkRef(isValidElement(children) ? (children as any).ref : null, ref);

  useIsomorphicEffect(() => {
    if (!disablePortal) {
      setMountNode(getContainer(container) || document.body);
    }
  }, [container, disablePortal]);

  useIsomorphicEffect(() => {
    if (mountNode && !disablePortal) {
      setRef(ref, mountNode);
      return () => {
        setRef(ref, null);
      };
    }

    return undefined;
  }, [ref, mountNode, disablePortal]);

  if (disablePortal) {
    if (isValidElement(children)) {
      const newProps = {
        ref: handleRef,
      };
      return cloneElement(children, newProps);
    }
    return <Fragment>{children}</Fragment>;
  }

  return <Fragment>{mountNode ? createPortal(children, mountNode) : mountNode}</Fragment>;
});

export default Portal;
